import axiosInstance from "../../utils/axiosConfig";
import React, { useCallback, useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { noTransaction } from "../../assets/images";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import { formatAmount } from "../../utils/FormatPerson";
import { BsArrowLeftSquare, BsArrowRightSquare } from "react-icons/bs";
import OtcDetailModal from "./OtcDetailModal";

const formatOtcTableDate = (tx: any) => {
  const dateString =
    tx.kind === "PAYOUT" ? tx.processedAt || tx.createdAt : tx.createdAt;
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = date.getDate();
  const ordinalSuffix = (d: number) => {
    if ([1, 21, 31].includes(d)) return "st";
    if ([2, 22].includes(d)) return "nd";
    if ([3, 23].includes(d)) return "rd";
    return "th";
  };
  return `${date.toLocaleString("default", {
    month: "short",
  })} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}, ${date.toLocaleString(
    "default",
    { hour: "numeric", minute: "numeric", hour12: true }
  )}`;
};

const truncateId = (id: string | undefined) => {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
};

const getStatusClass = (status: string) => {
  const s = (status || "").toUpperCase();
  const base =
    "inline-flex justify-center items-center shrink-0 px-2.5 py-1 tracking-wider min-h-[26px] rounded-full text-[10px] xs:text-[11px] font-bold whitespace-nowrap";
  if (s === "PENDING") return `text-[#CB9200] bg-[#181818] ${base}`;
  if (s === "PROCESSING") return `text-[#4FB0FF] bg-[#0D1F2D] ${base}`;
  if (s === "CONFIRMED") return `text-[#03C668] bg-[#0E2B1F] ${base}`;
  if (s === "COMPLETED") return `text-[#03C668] bg-[#0E2B1F] ${base}`;
  return `text-[#869AA9] bg-[#181818] ${base}`;
};

const OtcHistory = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchHistory = useCallback(() => {
    // The session bootstrap is async; fetching before it resolves just 401s.
    if (!isAuthenticated) return;
    setLoading(true);
    axiosInstance
      .get(`${API_URL}api/admin/otc/history?page=${page}&limit=25`)
      .then((r: any) => {
        const data = r.data?.data;
        setTransactions(data?.transactions ?? []);
        const meta = data?.meta ?? {};
        setCurrentPage(meta.page ?? page);
        setTotalPages(meta.totalPages ?? 1);
      })
      .catch((e: any) => {
        console.log(e.response?.data?.message);
        setTransactions([]);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated, page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const openDetail = (tx: any) => {
    setSelectedTransaction(tx);
    setShowDetailModal(true);
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedTransaction(null);
  };

  const renderAmount = (tx: any) => {
    if (tx.kind === "DEPOSIT") {
      return (
        <>
          {formatAmount(tx.amountCrypto)} {tx.currency}
          {tx.amountNGN != null && (
            <span className="text-[#869AA9] block text-[11px]">
              ₦{formatAmount(tx.amountNGN)}
            </span>
          )}
        </>
      );
    }
    return (
      <>
        {tx.asset?.amountCrypto != null &&
          `${formatAmount(tx.asset.amountCrypto)} ${tx.asset?.name ?? ""}`}
        {tx.payoutAmountNGN != null && (
          <span className="text-[#869AA9] block text-[11px]">
            ₦{formatAmount(tx.payoutAmountNGN)}
          </span>
        )}
        {!tx.asset?.amountCrypto &&
          tx.payoutAmountNGN != null &&
          `₦${formatAmount(tx.payoutAmountNGN)}`}
      </>
    );
  };

  return (
    <div className="w-full h-full">
      <div className="bg-[#0D0D0D] overflow-auto scrollbar-thumb-[#60B7F6] scrollbar-track-white mb-8 scrollbar-thin w-full min-h-[320px] md:h-[512px]">
        {loading ? (
          <div className="mt-12 w-full flex justify-center items-start">
            <TailSpin color="#0B75C2" height={70} width={90} />
          </div>
        ) : transactions.length < 1 ? (
          <div className="flex flex-col justify-center mt-12 w-full lgss:mt-12 items-center">
            <img src={noTransaction} alt="" />
            <h4 className="font-bold text-[#869AA9] pt-2">
              No OTC transactions
            </h4>
            <p className="text-[#869AA9] pt-2 text-[12px]">
              OTC history will appear here
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden w-full p-3 space-y-3 pb-6">
              {transactions.map((tx, index) => (
                <div
                  key={tx._id || index}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDetail(tx)}
                  onKeyDown={(e) => e.key === "Enter" && openDetail(tx)}
                  className="w-full rounded-xl bg-[#1C2227] p-4 cursor-pointer hover:bg-[#273037] transition active:opacity-90"
                >
                  <div className="min-w-0">
                    <p className="text-[#869AA9] text-[11px] uppercase tracking-wide">
                      {formatOtcTableDate(tx)}
                    </p>
                    <p className="text-white font-semibold text-[14px] mt-0.5 capitalize">
                      {tx.kind === "DEPOSIT" ? "Deposit" : "Payout"}
                    </p>
                    <p className="text-white/80 text-[13px] mt-1">
                      {tx.kind === "DEPOSIT"
                        ? tx.currency
                        : tx.asset?.name ?? "—"}{" "}
                      · {renderAmount(tx)}
                    </p>
                    <p className="mt-2">
                      <span className={getStatusClass(tx.status)}>
                        {tx.status}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block w-full">
              <table className="w-full">
                <thead>
                  <tr className="h-[56px] bg-[#1C2227] flex w-full px-4 lg:px-6 items-center sticky top-0">
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-1 min-w-0 py-2 pr-3 text-left whitespace-nowrap">
                      Date
                    </th>
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-1 min-w-0 py-2 pr-3 text-center whitespace-nowrap">
                      Type
                    </th>
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-1 min-w-0 py-2 pr-3 text-left whitespace-nowrap">
                      ID
                    </th>
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-1 min-w-0 py-2 pr-3 text-left whitespace-nowrap">
                      User ID
                    </th>
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-1 min-w-0 py-2 pr-3 text-left whitespace-nowrap">
                      Asset
                    </th>
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-[1.2] min-w-0 py-2 pr-3 text-left whitespace-nowrap">
                      Amount
                    </th>
                    <th className="text-[#617889] text-[12px] lg:text-[15px] flex-1 min-w-0 py-2 pr-3 text-left whitespace-nowrap">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="w-full">
                  {transactions.map((tx, index) => (
                    <React.Fragment key={tx._id || index}>
                      <tr
                        className="h-[56px] flex w-full px-4 lg:px-6 items-center cursor-pointer hover:bg-[#1C2227]"
                        onClick={() => openDetail(tx)}
                      >
                        <td className="text-white text-[12px] lg:text-[14px] flex-1 min-w-0 py-2 pr-3 overflow-hidden whitespace-nowrap">
                          {formatOtcTableDate(tx)}
                        </td>
                        <td className="text-white text-[12px] lg:text-[14px] flex-1 min-w-0 py-2 pr-3 overflow-hidden whitespace-nowrap capitalize text-center">
                          {tx.kind === "DEPOSIT" ? "Deposit" : "Payout"}
                        </td>
                        <td className="text-white text-[12px] lg:text-[14px] flex-1 min-w-0 py-2 pr-3 overflow-hidden whitespace-nowrap">
                          {truncateId(tx._id)}
                        </td>
                        <td className="text-white text-[12px] lg:text-[14px] flex-1 min-w-0 py-2 pr-3 overflow-hidden whitespace-nowrap">
                          {truncateId(tx.userId)}
                        </td>
                        <td className="text-white text-[12px] lg:text-[14px] flex-1 min-w-0 py-2 pr-3 overflow-hidden whitespace-nowrap uppercase">
                          {tx.kind === "DEPOSIT"
                            ? tx.currency
                            : tx.asset?.name ?? "—"}
                        </td>
                        <td className="text-white text-[12px] lg:text-[14px] flex-[1.2] min-w-0 py-2 pr-3 overflow-hidden whitespace-nowrap">
                          {renderAmount(tx)}
                        </td>
                        <td className="text-white text-[12px] lg:text-[14px] flex-1 min-w-0 py-2 pr-3 overflow-hidden flex justify-start items-center">
                          <span className={getStatusClass(tx.status)}>
                            {tx.status}
                          </span>
                        </td>
                      </tr>
                      <tr className="w-full">
                        <td
                          className="mt-1 w-full h-[1px] bg-[#13181B]"
                          colSpan={7}
                        />
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {transactions.length >= 1 && (
        <div className="flex flex-wrap gap-3 sm:gap-4 pb-10 justify-end items-center">
          <h4 className="font-bold font-sora text-[12px] sm:text-[14px] text-white">
            Page {currentPage} of {totalPages}
          </h4>
          <button
            type="button"
            className="flex justify-center items-center"
            onClick={() => page > 1 && setPage((p) => p - 1)}
          >
            <BsArrowLeftSquare
              className={`text-3xl font-bold ${
                page > 1
                  ? "cursor-pointer text-[#C6D0D7]"
                  : "text-[#3A4852] cursor-not-allowed"
              }`}
            />
          </button>
          <button
            type="button"
            className="flex justify-center items-center"
            onClick={() => page < totalPages && setPage((p) => p + 1)}
          >
            <BsArrowRightSquare
              className={`text-3xl ${
                page < totalPages
                  ? "cursor-pointer text-[#C6D0D7]"
                  : "text-[#3A4852] cursor-not-allowed"
              }`}
            />
          </button>
        </div>
      )}

      {showDetailModal && selectedTransaction && (
        <OtcDetailModal
          transaction={selectedTransaction}
          onClose={closeDetail}
          onCompleteSuccess={fetchHistory}
        />
      )}
    </div>
  );
};

export default OtcHistory;
